/**
 * Pure, unit-testable helpers for restoring query-state from localStorage and URL search params.
 *
 * Extracted from `querystate.ts` so that the type-coercion and restore logic can be tested in isolation
 * without a React rendering environment.
 */

/**
 * A field-specific transform function that receives either the raw URL param string value (when
 * the value contains no comma) or the comma-split string array (when it does), and returns the
 * coerced/transformed value for that field (e.g. `ensureArray` for status filter fields).
 *
 * The wider `string | string[]` input signature matches `ensureArray`'s actual generic signature
 * and allows multi-value round-trips through `?key=A,B` to work correctly.
 */
export type FieldEnhancer = (value: string | string[]) => unknown;

/**
 * Returns `true` when `value` is a positive integer (finite, > 0, no fractional part).
 *
 * Used to sanitize `companyId` values coming from URL params or localStorage before they
 * are forwarded to the backend. The backend ownership check remains the real security guard;
 * this is a minimal, non-regressing defensive layer in the pure parser.
 */
function isPositiveInteger(value: unknown): boolean {
    if (typeof value !== "number") {
        return false;
    }
    return Number.isFinite(value) && value > 0 && Number.isInteger(value);
}

/**
 * Parses a single URL search-param value and coerces it to the appropriate JavaScript type.
 *
 * Priority (highest to lowest):
 * 1. If an `enhancer` function is registered for this `key`, the value is comma-split FIRST (when it
 *    contains a comma), and the resulting `string[]` (or the original scalar `string` when there is no
 *    comma) is forwarded to the enhancer — before any numeric/boolean coercion. This ensures that
 *    multi-value fields like `status` round-trip correctly through `?status=A,B` → `["A","B"]`.
 * 2. If the value contains a comma it is split into a string array.
 * 3. If the value is a finite number string (and non-blank) it is coerced to `Number`.
 * 4. If the value is the literal string `"true"` or `"false"` it is coerced to `Boolean`.
 * 5. Otherwise the raw string is returned.
 *
 * @param key     - The URL parameter key.
 * @param value   - The raw string value from `URLSearchParams`.
 * @param enhancer - Optional map of field-specific transform functions. When present and the key has a
 *                   registered function, the comma-split value (or the scalar string when no comma) is
 *                   passed to the enhancer and its return value is used directly — no further coercion
 *                   is applied.
 */
export function parseSearchParamValue(
    key: string,
    value: string,
    enhancer?: Record<string, FieldEnhancer>
): unknown {
    // Enhancer takes full priority — it runs before any other coercion.
    // Split comma-joined multi-values into individual entries BEFORE the enhancer so that
    // array fields (ensureArray) round-trip correctly through `?key=A,B`; a single value
    // stays scalar and the enhancer wraps it. Runs before numeric/boolean coercion so a
    // single enum value is never mis-coerced.
    const fn = enhancer?.[key];
    if (fn) {
        const pre = value.includes(",") ? value.split(",").filter(Boolean) : value;
        return fn(pre);
    }

    // Comma-separated list → string array
    if (value.includes(",")) {
        return value.split(",").filter(Boolean);
    }

    // Numeric string → Number (keep 0, but guard against blank)
    if (value.trim() !== "" && !Number.isNaN(Number(value))) {
        const num = Number(value);
        // Defensive: companyId must be a positive integer; drop any other value.
        if (key === "companyId" && !isPositiveInteger(num)) {
            return undefined;
        }
        return num;
    }

    // Boolean string → boolean
    if (value === "true" || value === "false") {
        return value === "true";
    }

    return value;
}

/**
 * Converts all entries from a `URLSearchParams` (or any `[string, string][]` iterable) into a typed
 * partial object by calling `parseSearchParamValue` for each entry.
 *
 * @param entries  - The URL parameter entries (e.g. `Array.from(searchParams.entries())`).
 * @param enhancer - Optional field-level enhancer map forwarded to `parseSearchParamValue`.
 */
export function parseSearchParams<T extends object>(
    entries: Iterable<[string, string]>,
    enhancer?: Record<string, FieldEnhancer>
): Partial<T> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of entries) {
        result[key] = parseSearchParamValue(key, value, enhancer);
    }
    return result as Partial<T>;
}

/**
 * Reads and JSON-parses a value from `localStorage` by item name.
 *
 * Returns `undefined` when:
 * - `itemName` is `undefined` or `null`
 * - The item does not exist in storage
 * - The stored value is not valid JSON
 * - The parsed value is `null`
 *
 * @param itemName - The localStorage key to read.
 */
export function readRestoredState<T>(itemName: string | undefined | null): T | undefined {
    if (itemName == null) {
        return undefined;
    }
    const raw = localStorage.getItem(itemName);
    if (raw == null) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed == null) {
            return undefined;
        }
        // Guard: only accept plain objects — primitives (42, "foo", true) and arrays stored
        // in localStorage are not valid query-state shapes and are dropped.
        if (typeof parsed !== "object" || Array.isArray(parsed)) {
            return undefined;
        }
        return parsed as T;
    } catch {
        return undefined;
    }
}

/**
 * Computes the state to restore from `store` (previously read from localStorage) when the URL is
 * currently empty (i.e. no search params are present).
 *
 * `companyId` is always stripped from the restored state to avoid re-applying a stale company filter
 * that belongs to a different user session.
 *
 * Returns `undefined` when:
 * - `store` is `undefined` / falsy
 * - `store` is an empty object `{}` (nothing meaningful to restore)
 * - `store` contains only `companyId` (after stripping, the restore state would be empty — no
 *   meaningful filter to apply, so we avoid a spurious `setSearchParams({})` call on reload)
 * - `urlIsEmpty` is `false` (the URL already carries params → the URL is the source of truth)
 *
 * @param store      - The value previously persisted in localStorage, or `undefined` when absent.
 * @param urlIsEmpty - Whether the current URL has no search params.
 */
export function computeRestoredState<T extends object>(
    store: T | undefined,
    urlIsEmpty: boolean
): Omit<T, "companyId"> | undefined {
    if (!store || !urlIsEmpty) {
        return undefined;
    }
    // Guard: empty-object store has nothing meaningful to restore.
    if (Object.keys(store).length === 0) {
        return undefined;
    }
    const { companyId: _companyId, ...restoreState } = store as T & { companyId?: unknown };
    // Guard: if the only key was companyId, the remaining state is empty — no meaningful restore.
    if (Object.keys(restoreState).length === 0) {
        return undefined;
    }
    return restoreState;
}

/**
 * Validates a `companyId` value read directly from localStorage-restored state (outside of URL
 * param parsing). Returns the value unchanged when it is a positive integer, or `undefined`
 * to drop it when it is not.
 *
 * This is used internally by consumers that read the raw stored object before it goes through
 * `parseSearchParamValue`. `computeRestoredState` already strips `companyId` from the restore
 * state, so this guard is a defence-in-depth helper for direct storage reads.
 *
 * @param value - The raw companyId value from localStorage.
 */
export function sanitizeCompanyId(value: unknown): number | undefined {
    if (isPositiveInteger(value)) {
        return value as number;
    }
    return undefined;
}
